import { createContext, useContext, useState, useCallback } from 'react';

const CURRENCY_RATES = {
  USD: 1.0,
  INR: 83.50,
  GBP: 0.79,
  EUR: 0.92
};

const CURRENCY_SYMBOLS = {
  USD: '$',
  INR: '',
  GBP: '£',
  EUR: ''
};

function formatIndianNumber(num) {
  const str = Math.round(num).toString();
  if (str.length <= 3) return str;
  let last3 = str.slice(-3);
  let remaining = str.slice(0, -3);
  // Add commas every 2 digits for Indian numbering
  remaining = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return remaining + ',' + last3;
}

function formatCroresLabel(num) {
  if (num >= 10000000) {
    const cr = num / 10000000;
    return cr % 1 === 0 ? `${cr} Cr` : `${cr.toFixed(1)} Cr`;
  }
  if (num >= 100000) {
    const lakh = num / 100000;
    return lakh % 1 === 0 ? `${lakh} Lac` : `${lakh.toFixed(1)} Lac`;
  }
  return formatIndianNumber(num);
}

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState('INR');

  const convertPrice = useCallback((priceInUSD, toCurrency = currency) => {
    const rate = CURRENCY_RATES[toCurrency] || 1;
    return Math.round(priceInUSD * rate);
  }, [currency]);

  const formatPrice = useCallback((priceInUSD, toCurrency = currency) => {
    const converted = convertPrice(priceInUSD, toCurrency);
    const symbol = CURRENCY_SYMBOLS[toCurrency] || '$';
    
    if (toCurrency === 'INR') {
      return `${formatCroresLabel(converted)}`;
    }
    if (toCurrency === 'EUR') {
      return `${converted.toLocaleString()}`;
    }
    return `${symbol}${converted.toLocaleString()}`;
  }, [convertPrice, currency]);

  const value = {
    currency,
    setCurrency,
    convertPrice,
    formatPrice,
    currencies: Object.keys(CURRENCY_RATES),
    symbols: CURRENCY_SYMBOLS
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
